import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { ProjectDetailContext } from "@/contexts/ProjectDetailContext";
import { DocumentApiResponse, DocumentCreatePayload, useCheckDuplicates, useSaveDocumentMetadata } from "@/hooks/useDocumentMetadata";
import { useFileUpload } from "@/hooks/useFileUpload";
import { useIndexingQueue } from "@/hooks/useIndexingQueue";
import { useMetadataQueue } from "@/hooks/useMetadataQueue";
import { useInsertProjectDocuments } from "@/hooks/useProjects";
import { applyTotalSizeLimit, FileValidationError, getFileErrorReason, MAX_FILE_SIZE_MB, MAX_PAGE_COUNT, MAX_TOTAL_UPLOAD_SIZE_GB, UPLOAD_LIMIT_ERROR_MESSAGE, validateFiles } from "@/lib/fileUtils";
import { recordDocumentEvents } from "@/lib/evidenceAudit";
import { cn } from "@/lib/utils";
import { DuplicateCheckResult } from "@/services/evidenceBaseApi";
import { AlertCircle, Check, CheckCircle2, ChevronDown, ChevronRight, ChevronsUpDown, Clock, FileText, FileUp, Loader2, RefreshCw, Upload, X } from "lucide-react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";

import TagsInput from "@/components/TagsInput";
import { DatePicker } from "@/components/ui/date-picker";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEvidenceTypes, useNetsuiteClients, useSourceTypes } from "@/hooks/useMasterData";
import TopicsMultiSelect from "@/components/TopicsMultiSelect";
import { OWNERSHIP_VALUES } from "@/types/models";

type ExtractionStatus = "idle" | "queued" | "processing" | "extracting" | "done" | "failed";

interface DocumentFormEntry {
  file: File;
  uploadedFilename: string;
  downloadUrl: string;
  title: string;
  authors: string;
  year: string;
  description: string;
  source_type: string;
  organization: string;
  country: string;
  evidence_type: string;
  data_retention: string | null;
  url: string;
  language: string;
  ownership: string;
  client_id: string;
  client_name: string;
  tags: string;
  topic_ids: string[];
}

interface DocumentFormValues {
  documents: DocumentFormEntry[];
}

const selectClassName =
  "w-full appearance-none border rounded-md px-3 py-2 text-sm bg-card focus:outline-none focus:ring-2 focus:ring-brand-teal/30";

interface DocumentUploadFormProps {
  projectId?: string;
  onComplete?: () => void;
  lockedOwnership?: string;
  allowedOwnershipValues?: string[];
  /** Pre-populate the form with these files on mount (e.g. dropped on an outer dropzone before this form was mounted). */
  initialFiles?: File[];
  /** Hide the internal page-title/description header — use when the caller already renders its own heading (e.g. a Dialog title). */
  hideHeader?: boolean;
  /** Show the "Select file(s) from Evidence Base" escape-hatch button. Defaults to true to preserve existing behavior. */
  showEvidenceBaseEscapeHatch?: boolean;
}

const DocumentUploadForm = ({
  projectId: propProjectId,
  onComplete,
  lockedOwnership,
  allowedOwnershipValues,
  initialFiles,
  hideHeader = false,
  showEvidenceBaseEscapeHatch = true,
}: DocumentUploadFormProps = {}) => {
  const ctx = useContext(ProjectDetailContext);
  const projectId = propProjectId ?? ctx?.projectId ?? "";
  // Already on this project, so visible to the uploader whoever added them.
  const linkedDocumentIds = useMemo(() => (ctx?.projectDocuments ?? []).map((d) => d.id), [ctx?.projectDocuments]);
  const project = ctx?.project;
  const activeStage = ctx?.activeStage ?? 0;
  const handleStageComplete = onComplete ? () => onComplete() : (ctx?.handleStageComplete ?? (() => { }));
  const continueLabel = ctx?.continueLabel ?? 'Continue';
  const pageTitle = ctx?.pageTitle ?? 'Document Upload';
  const [dragActive, setDragActive] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  // Only the expanded row's DatePicker is ever mounted (accordion), so a single
  // shared flag/ref tracks whichever one is currently showing an unresolved error.
  const [activeDataRetentionInvalid, setActiveDataRetentionInvalid] = useState(false);
  const activeDataRetentionRef = useRef<HTMLDivElement>(null);
  const [filesUploaded, setFilesUploaded] = useState(false);
  const [isEnqueuingExtraction, setIsEnqueuingExtraction] = useState(false);
  const [extractionStatus, setExtractionStatus] = useState<Record<number, ExtractionStatus>>({});
  // Map from original form field index to saved document ID so that indexing
  // status badges always render under the correct field, even when some
  // duplicates are skipped.
  const [savedDocIds, setSavedDocIds] = useState<Record<number, string>>({});
  const [submitStage, setSubmitStage] = useState<"idle" | "saving" | "indexing" | "linking">("idle");
  const [validationErrors, setValidationErrors] = useState<FileValidationError[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const populatedDocIdsRef = useRef<Set<string>>(new Set());

  // Per-file client combobox — one popover open at a time
  const [openClientPopoverIndex, setOpenClientPopoverIndex] = useState<number | null>(null);
  const [clientInputValue, setClientInputValue] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const clientDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: clientResults = [], isFetching: clientFetching } = useNetsuiteClients(clientSearch);
  // Duplicate detection state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateResults, setDuplicateResults] = useState<DuplicateCheckResult[]>([]);
  const [duplicateConfirmed, setDuplicateConfirmed] = useState<Record<number, boolean>>({});
  const [pendingFormData, setPendingFormData] = useState<DocumentFormValues | null>(null);

  const fileUpload = useFileUpload();
  const indexingQueue = useIndexingQueue();
  const saveMetadata = useSaveDocumentMetadata();
  const checkDuplicates = useCheckDuplicates();
  const queue = useMetadataQueue();
  const insertProjectDocuments = useInsertProjectDocuments();

  const { data: sourceTypes = [] } = useSourceTypes();
  const { data: evidenceTypes = [] } = useEvidenceTypes();

  const activeSourceTypes = sourceTypes.filter(st => st.is_active);
  const activeEvidenceTypes = evidenceTypes.filter(et => et.is_active);

  // Per-file topics popover — one open at a time
  const [openTopicsPopoverIndex, setOpenTopicsPopoverIndex] = useState<number | null>(null);

  const { control, handleSubmit, reset, setValue, getValues } = useForm<DocumentFormValues>({
    defaultValues: { documents: [] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "documents",
  });

  const addFiles = async (incoming: FileList | File[] | null) => {
    if (!incoming) return;
    const fileArray = Array.from(incoming);

    // Validate files before adding
    setIsValidating(true);
    setValidationErrors([]);
    let passingFiles = fileArray;
    try {
      const errors = await validateFiles(fileArray);
      if (errors.length > 0) {
        setValidationErrors(errors);
        // Separate passing files from failing ones instead of discarding entire batch
        const failedNames = new Set(errors.map((e) => e.fileName));
        passingFiles = fileArray.filter((f) => !failedNames.has(f.name));
      }
    } catch {
      // If validation itself fails, allow files through (backend will catch)
    }

    // Every file in the form goes up in one request, so count the ones already added
    const existingBytes = getValues("documents").reduce((sum, doc) => sum + doc.file.size, 0);
    const { accepted, rejected } = applyTotalSizeLimit(passingFiles, existingBytes);
    if (rejected.length > 0) {
      setValidationErrors((prev) => [...prev, ...rejected]);
      passingFiles = accepted;
    }
    setIsValidating(false);

    if (passingFiles.length === 0) return;

    const newEntries: DocumentFormEntry[] = passingFiles.map((file) => ({
      file,
      uploadedFilename: "",
      downloadUrl: "",
      title: "",
      authors: "",
      year: "",
      description: "",
      source_type: "",
      organization: "",
      country: "",
      evidence_type: "",
      data_retention: project?.dataRetentionDefault ?? null,
      url: "",
      language: "",
      ownership: lockedOwnership ?? (project?.clientIds?.[0] ? "Client" : "Private"), // "Public" hidden: to restore change "Private" to "Public" in the final branch
      client_id: project?.clientIds?.[0] ?? "",
      client_name: project?.clientNames?.[0] ?? "",
      tags: "",
      topic_ids: [],
    }));
    append(newEntries);
    if (fields.length === 0 && newEntries.length > 0) {
      setExpandedIndex(0);
    }
    setFilesUploaded(false);
  };

  const seededInitialFilesRef = useRef(false);
  useEffect(() => {
    if (initialFiles?.length && !seededInitialFilesRef.current) {
      seededInitialFilesRef.current = true;
      addFiles(initialFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFiles]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(e.dataTransfer.files);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    remove(index);
    if (expandedIndex === index) {
      setExpandedIndex(null);
      setActiveDataRetentionInvalid(false);
    } else if (expandedIndex !== null && expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  // Step 1: Upload files, then enqueue AI metadata extraction via Service Bus
  const handleUpload = () => {
    const data = getValues();
    if (data.documents.length === 0) return;

    const files = data.documents.map((d) => d.file);

    fileUpload.mutate(files, {
      onSuccess: async (response) => {
        const uploadResult = Array.isArray(response) ? response : [];
        uploadResult.forEach((r, i) => {
          setValue(`documents.${i}.uploadedFilename`, r.filename);
          setValue(`documents.${i}.downloadUrl`, r.download_url);
        });
        setFilesUploaded(true);
        setIsEnqueuingExtraction(true);
        setExpandedIndex(0);

        // Build enqueue requests — generate a UUID per file as document_id
        const extractRequests = uploadResult.map((r, i) => ({
          document_id: crypto.randomUUID(),
          filename: r.filename,
          original_name: data.documents[i].file.name,
          download_url: r.download_url,
          model: project?.model,
        }));

        try {
          populatedDocIdsRef.current = new Set();
          const mapping = await queue.enqueue(extractRequests, projectId);

          // Mark all as queued immediately
          const initialStatus: Record<number, ExtractionStatus> = {};
          mapping.forEach((_, i) => { initialStatus[i] = "queued"; });
          setExtractionStatus(initialStatus);
          setIsEnqueuingExtraction(false);

          const docIds = mapping.map((m) => m.document_id);
          queue.startPolling(docIds, (statuses) => {
            const updatedStatus: Record<number, ExtractionStatus> = {};
            mapping.forEach((m, i) => {
              const info = statuses[m.document_id];
              const result = info?.result;
              if (result) {
                if (result.title) setValue(`documents.${i}.title`, result.title);
                if (result.author) setValue(`documents.${i}.authors`, result.author);
                if (result.year) setValue(`documents.${i}.year`, result.year);
                if (result.description) setValue(`documents.${i}.description`, result.description);
                if (result.source_type) setValue(`documents.${i}.source_type`, result.source_type);
                if (result.source_organisation) setValue(`documents.${i}.organization`, result.source_organisation);
                if (result.country) setValue(`documents.${i}.country`, result.country);
                if (result.evidence_type) setValue(`documents.${i}.evidence_type`, result.evidence_type);
                if (result.url) setValue(`documents.${i}.url`, result.url);
                if (result.language) setValue(`documents.${i}.language`, result.language);
                if (result.tags?.length) setValue(`documents.${i}.tags`, result.tags.join(", "));
              }
              updatedStatus[i] = info?.status === "Done" ? "done" : "failed";
            });
            setExtractionStatus(updatedStatus);
          });
        } catch {
          setIsEnqueuingExtraction(false);
          const failedStatus: Record<number, ExtractionStatus> = {};
          uploadResult.forEach((_, i) => { failedStatus[i] = "failed"; });
          setExtractionStatus(failedStatus);
        }
      },
    });
  };

  // Sync per-file extractionStatus from live queue.documentStatuses during polling,
  // and populate form fields immediately when each document's extraction completes.
  useEffect(() => {
    if (!queue.fileDocumentMap.length) return;
    const next: Record<number, ExtractionStatus> = {};
    queue.fileDocumentMap.forEach((entry, i) => {
      const info = queue.documentStatuses[entry.document_id];
      if (!info) return;
      next[i] =
        info.status === "Queued" ? "queued" :
          info.status === "Processing" ? "processing" :
            info.status === "Done" ? "done" : "failed";

      if (info.status === "Done" && info.result && !populatedDocIdsRef.current.has(entry.document_id)) {
        populatedDocIdsRef.current.add(entry.document_id);
        const result = info.result;
        if (result.title) setValue(`documents.${i}.title`, result.title);
        if (result.author) setValue(`documents.${i}.authors`, result.author);
        if (result.year) setValue(`documents.${i}.year`, result.year);
        if (result.description) setValue(`documents.${i}.description`, result.description);
        if (result.source_type) setValue(`documents.${i}.source_type`, result.source_type);
        if (result.source_organisation) setValue(`documents.${i}.organization`, result.source_organisation);
        if (result.country) setValue(`documents.${i}.country`, result.country);
        if (result.evidence_type) setValue(`documents.${i}.evidence_type`, result.evidence_type);
        if (result.url) setValue(`documents.${i}.url`, result.url);
        if (result.language) setValue(`documents.${i}.language`, result.language);
        if (result.tags?.length) setValue(`documents.${i}.tags`, result.tags.join(", "));
      }
    });
    setExtractionStatus((prev) => ({ ...prev, ...next }));
  }, [queue.documentStatuses, queue.fileDocumentMap, setValue]);

  // On mount: restore any in-progress extractions for this project from the backend
  useEffect(() => {
    if (!projectId) return;
    queue.fetchPendingJobs(projectId).then((pending) => {
      if (!pending.jobs.length) return;
      setFilesUploaded(true);
      setExpandedIndex(0);

      // Restore form entries from pending job data
      const restored = pending.jobs.map((job) => ({
        file: new File([], job.original_name ?? job.filename),
        uploadedFilename: job.filename,
        downloadUrl: job.download_url ?? "",
        title: "", authors: "", year: "", description: "",
        source_type: "", organization: "", country: "",
        evidence_type: "", data_retention: project?.dataRetentionDefault ?? null, url: "",
        language: "", ownership: lockedOwnership ?? (project?.clientIds?.[0] ? "Client" : project ? "Private" : "Public"),
        client_id: project?.clientIds?.[0] ?? "", client_name: project?.clientNames?.[0] ?? "", tags: "",
        topic_ids: [],
      }));
      reset({ documents: restored });

      const restoredMap = pending.jobs.map((job) => ({
        filename: job.filename,
        document_id: job.document_id,
      }));
      // Seed statuses
      const restoredStatuses: Record<number, ExtractionStatus> = {};
      pending.jobs.forEach((job, i) => {
        restoredStatuses[i] = (job.status === "Queued" ? "queued" : "processing") as ExtractionStatus;
      });
      setExtractionStatus(restoredStatuses);

      const docIds = pending.jobs.map((j) => j.document_id);
      queue.startPolling(docIds, (statuses) => {
        const updatedStatus: Record<number, ExtractionStatus> = {};
        restoredMap.forEach((m, i) => {
          const info = statuses[m.document_id];
          const result = info?.result;
          if (result) {
            if (result.title) setValue(`documents.${i}.title`, result.title);
            if (result.author) setValue(`documents.${i}.authors`, result.author);
            if (result.year) setValue(`documents.${i}.year`, result.year);
            if (result.description) setValue(`documents.${i}.description`, result.description);
            if (result.source_type) setValue(`documents.${i}.source_type`, result.source_type);
            if (result.source_organisation) setValue(`documents.${i}.organization`, result.source_organisation);
            if (result.country) setValue(`documents.${i}.country`, result.country);
            if (result.evidence_type) setValue(`documents.${i}.evidence_type`, result.evidence_type);
            if (result.url) setValue(`documents.${i}.url`, result.url);
            if (result.language) setValue(`documents.${i}.language`, result.language);
            if (result.tags?.length) setValue(`documents.${i}.tags`, result.tags.join(", "));
          }
          updatedStatus[i] = info?.status === "Done" ? "done" : "failed";
        });
        setExtractionStatus(updatedStatus);
      });
    }).catch(() => {/* silently ignore — non-critical */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const retryExtraction = async (index: number) => {
    const doc = getValues(`documents.${index}`);
    if (!doc.uploadedFilename) return;
    const existingEntry = queue.fileDocumentMap[index];
    const documentId = existingEntry?.document_id || crypto.randomUUID();

    // If fileDocumentMap doesn't have an entry at this index (e.g. initial enqueue
    // failed), register the new document_id so the useEffect that syncs
    // fileDocumentMap[i] → extractionStatus[i] can track intermediate statuses.
    if (!existingEntry) {
      queue.updateFileDocumentEntry(index, { filename: doc.uploadedFilename, document_id: documentId });
    }

    setExtractionStatus((prev) => ({ ...prev, [index]: "queued" }));
    try {
      await queue.retryDocument(
        { document_id: documentId, filename: doc.uploadedFilename, download_url: doc.downloadUrl, model: project?.model },
        projectId,
        (info) => {
          const result = info?.result;
          if (result) {
            if (result.title) setValue(`documents.${index}.title`, result.title);
            if (result.author) setValue(`documents.${index}.authors`, result.author);
            if (result.year) setValue(`documents.${index}.year`, result.year);
            if (result.description) setValue(`documents.${index}.description`, result.description);
            if (result.source_type) setValue(`documents.${index}.source_type`, result.source_type);
            if (result.source_organisation) setValue(`documents.${index}.organization`, result.source_organisation);
            if (result.country) setValue(`documents.${index}.country`, result.country);
            if (result.evidence_type) setValue(`documents.${index}.evidence_type`, result.evidence_type);
            if (result.url) setValue(`documents.${index}.url`, result.url);
            if (result.language) setValue(`documents.${index}.language`, result.language);
            if (result.tags?.length) setValue(`documents.${index}.tags`, result.tags.join(", "));
          }
          setExtractionStatus((prev) => ({ ...prev, [index]: info?.status === "Done" ? "done" : "failed" }));
        },
      );
    } catch {
      setExtractionStatus((prev) => ({ ...prev, [index]: "failed" }));
    }
  };

  // Helper: proceed with saving a subset of documents (filtered by indices)
  const proceedWithSave = (data: DocumentFormValues, indicesToSave: number[]) => {
    if (indicesToSave.length === 0) return;

    setSubmitStage("saving");

    const filteredDocs = indicesToSave.map((i) => data.documents[i]);

    const payloads: DocumentCreatePayload[] = filteredDocs.map((doc) => ({
      filename: doc.uploadedFilename || doc.file.name,
      title: doc.title || null,
      authors: doc.authors || null,
      year: doc.year || null,
      description: doc.description || null,
      source_type: doc.source_type || null,
      organization: doc.organization || null,
      country: doc.country || null,
      evidence_type: doc.evidence_type || null,
      data_retention: doc.data_retention || null,
      url: doc.url || null,
      language: doc.language || null,
      ownership: doc.ownership || null,
      client_id: doc.ownership === "Client" ? (doc.client_id || null) : null,
      tags: doc.tags || null,
      topic_ids: doc.topic_ids,
    }));

    saveMetadata.mutate(payloads, {
      onSuccess: async (savedDocuments: DocumentApiResponse[]) => {
        const docIds = savedDocuments.map((d) => d.id);

        // Build a map from original form field index → saved document ID.
        // savedDocuments[k] corresponds to indicesToSave[k].
        const idMap: Record<number, string> = {};
        indicesToSave.forEach((originalIdx, k) => {
          idMap[originalIdx] = savedDocuments[k].id;
        });
        setSavedDocIds(idMap);
        // Set before the enqueue await below so this batches with the
        // save mutation's isPending→false render — no all-idle frame in between.
        setSubmitStage("indexing");

        // Enqueue indexing jobs and start per-document polling
        const jobs = savedDocuments.map((doc) => ({
          document_id: doc.id,
          filename: doc.filename,
          doc_action: "ingest",
        }));

        try {
          await indexingQueue.enqueue(jobs, projectId);
          indexingQueue.startPolling(docIds, () => {
            // Batches with indexingQueue's isPolling→false render for the same reason.
            setSubmitStage("linking");
            // After all documents are indexed, link them to the project
            // mutateAsync rather than per-call callbacks, which are dropped if the form
            // unmounts mid-link — the link still lands, so its audit entry must too.
            insertProjectDocuments
              .mutateAsync({ projectId, documentIds: docIds })
              .then(() => {
                if (projectId) recordDocumentEvents(projectId, "upload_document", savedDocuments);
              })
              .catch(() => {
                // Documents are saved and indexed; proceed even if linking fails
              })
              .finally(() => {
                setSubmitStage("idle");
                reset({ documents: [] });
                setExpandedIndex(null);
                setFilesUploaded(false);
                setSavedDocIds({});
                handleStageComplete(activeStage);
              });
          });
        } catch {
          // Enqueue failed — surface via indexingQueue error state
          setSubmitStage("idle");
        }
      },
      onError: () => {
        setSubmitStage("idle");
      },
    });
  };

  // Handle duplicate dialog confirmation
  const handleDuplicateConfirm = () => {
    if (!pendingFormData) return;
    // Read live form values so edits made while the duplicate dialog was open are captured
    const liveData = getValues();
    const allIndices = liveData.documents.map((_, i) => i);
    // Non-duplicates always proceed; duplicates only if user confirmed them
    const indicesToSave = allIndices.filter((i) => {
      const dupResult = duplicateResults.find((r) => r.index === i);
      if (!dupResult || !dupResult.is_duplicate) return true;
      return duplicateConfirmed[i] === true;
    });
    setShowDuplicateDialog(false);
    setDuplicateResults([]);
    setDuplicateConfirmed({});
    if (indicesToSave.length > 0) {
      proceedWithSave(liveData, indicesToSave);
    }
    setPendingFormData(null);
  };

  // Handle duplicate dialog cancellation (cancel all)
  const handleDuplicateCancelAll = () => {
    setShowDuplicateDialog(false);
    setDuplicateResults([]);
    setDuplicateConfirmed({});
    setPendingFormData(null);
  };

  // Step 2: Save metadata, then index, then advance
  const onSubmit = (data: DocumentFormValues) => {
    if (data.documents.length === 0) return;

    if (activeDataRetentionInvalid) {
      activeDataRetentionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    // Check for duplicates first
    const checkItems = data.documents.map((doc) => ({
      title: doc.title || "",
      filename: doc.uploadedFilename || doc.file.name,
      authors: doc.authors || "",
      country: doc.country || "",
      year: doc.year || "",
      ownership: doc.ownership || null,
      client_id: doc.ownership === "Client" ? (doc.client_id || null) : null,
    }));

    checkDuplicates.mutate({ items: checkItems, visibleIds: linkedDocumentIds }, {
      onSuccess: (results) => {
        const safeResults = Array.isArray(results) ? results : [];
        const duplicates = safeResults.filter((r) => r.is_duplicate);
        if (duplicates.length > 0) {
          // Show confirmation dialog
          setDuplicateResults(safeResults);
          setPendingFormData(data);
          // Default: all duplicates unchecked (not confirmed)
          const initial: Record<number, boolean> = {};
          duplicates.forEach((d) => { initial[d.index] = false; });
          setDuplicateConfirmed(initial);
          setShowDuplicateDialog(true);
        } else {
          // No duplicates — proceed as normal
          const allIndices = data.documents.map((_, i) => i);
          proceedWithSave(data, allIndices);
        }
      },
      onError: () => {
        // If duplicate check fails, proceed anyway to not block the user
        const allIndices = data.documents.map((_, i) => i);
        proceedWithSave(data, allIndices);
      },
    });
  };

  const isUploading = fileUpload.isPending;
  const isExtracting = queue.isPolling;
  const isCheckingDuplicates = checkDuplicates.isPending;
  const isPending = isUploading || isExtracting || isCheckingDuplicates || submitStage !== "idle" || isValidating || isEnqueuingExtraction;

  // Check if any documents are still in a non-terminal extraction state.
  // This covers the case where polling has timed out but documents are still
  // stuck in "queued" or "processing" (before the timeout marks them as failed).
  const hasIncompleteExtraction = useMemo(() => {
    if (!filesUploaded) return false;
    const statuses = Object.values(extractionStatus);
    if (statuses.length === 0) return false;
    return statuses.some(
      (s) => s === "queued" || s === "processing" || s === "extracting"
    );
  }, [filesUploaded, extractionStatus]);

  // Derived counts for the progress banner
  const doneCount = Object.values(queue.documentStatuses).filter(
    (s) => s.status === "Done" || s.status === "Error",
  ).length;
  const totalCount = queue.fileDocumentMap.length;

  return (
    <div>
      <div className="flex flex-col-reverse md:flex-row justify-between mb-4">
        {!hideHeader && (
          <div>
            <h2 className="text-lg font-bold text-foreground">{pageTitle}</h2>
            <p className="text-sm text-muted-foreground">Upload documents to the project for indexing in the evidence base</p>
          </div>
        )}

        {/* Skip upload — go directly to evidence base selection */}
        {showEvidenceBaseEscapeHatch && !filesUploaded && (<div className="flex justify-end mt-2 mb-4">
          <Button
            type="button"
            variant="brand"
            disabled={isPending}
            onClick={() => handleStageComplete(0)}
          >
            Select file(s) from Evidence Base <ChevronRight className="w-4 h-4" />
          </Button>
        </div>)}
      </div>

      <div className="bg-brand-amber/10 border border-brand-amber/30 rounded-lg p-3 mb-6 text-sm">
        <span className="font-medium">💡 Tip:</span> Upload one or more documents. Metadata will be automatically extracted and can then be reviewed before saving.
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf"
        onChange={handleFileInput}
        className="hidden"
      />

      {/* File Drop Zone — hidden once files are uploaded */}
      {!filesUploaded && (
        <div
          className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors mb-6 ${dragActive ? "border-brand-teal bg-brand-teal/5" : "border-muted-foreground/30 hover:border-brand-teal/50"
            } ${isUploading || isValidating ? "pointer-events-none opacity-60" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          onClick={() => !isUploading && !isValidating && fileInputRef.current?.click()}
        >
          <FileUp className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-medium text-foreground mb-1">Drag & drop your files here</p>
          <p className="text-xs text-muted-foreground mb-3">PDF up to {MAX_PAGE_COUNT} pages / {MAX_FILE_SIZE_MB} MB, {MAX_TOTAL_UPLOAD_SIZE_GB} GB in total</p>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} disabled={isUploading || isValidating}>
            <Upload className="w-4 h-4" /> Browse Files
          </Button>
        </div>
      )}

      {/* Validation error message */}
      {validationErrors.length > 0 && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
          <p className="text-sm text-destructive font-medium flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {UPLOAD_LIMIT_ERROR_MESSAGE}
          </p>
          <ul className="mt-2 text-xs text-destructive/80 list-disc list-inside">
            {validationErrors.map((err, i) => (
              <li key={i}>
                <span className="font-medium">{err.fileName}</span>
                {` — ${getFileErrorReason(err)}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Validating indicator */}
      {isValidating && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm flex items-center gap-2 text-blue-700">
          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          Validating files…
        </div>
      )}

      {/* Status messages */}
      {fileUpload.isError && (
        <p className="mb-4 text-sm text-destructive">
          Upload failed: {fileUpload.error?.message}
        </p>
      )}
      {saveMetadata.isError && (
        <p className="mb-4 text-sm text-destructive">
          Metadata save failed: {saveMetadata.error?.message}
        </p>
      )}
      {Object.values(indexingQueue.documentStatuses).some((s) => s.status === "Error") && (
        <p className="mb-4 text-sm text-destructive">
          One or more documents failed to index. See status below.
        </p>
      )}



      {/* File list & metadata forms */}
      {fields.length > 0 && (
        <form onSubmit={handleSubmit(onSubmit)}>
          {filesUploaded && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              Files uploaded successfully. Fill in the metadata below, then save to index and continue.
            </div>
          )}

          {(isExtracting || isEnqueuingExtraction) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm flex items-center gap-2 text-blue-700">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              Extracting metadata {doneCount} of {totalCount}... Fields will auto-populate when done.
            </div>
          )}

          {/* Warning when extraction timed out with failures */}
          {!isExtracting && !isEnqueuingExtraction && filesUploaded && Object.values(extractionStatus).some((s) => s === "failed") && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm flex items-center gap-2 text-amber-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              Some documents failed metadata extraction. You can retry failed documents or fill in the metadata manually before continuing.
            </div>
          )}

          <div className="space-y-3 mb-6">
            {fields.map((field, index) => {
              const isExpanded = expandedIndex === index;
              return (
                <div key={field.id} className="bg-card border rounded-lg overflow-hidden">
                  {/* File header row */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <button
                      type="button"
                      className="flex items-center gap-2 truncate text-sm font-medium text-foreground hover:text-brand-teal transition-colors"
                      onClick={() => {
                        // Whichever row's DatePicker is about to unmount can't stay "invalid"
                        setActiveDataRetentionInvalid(false);
                        setExpandedIndex(isExpanded ? null : index);
                      }}
                    >
                      {isExpanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                      <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="truncate">{field.file.name}</span>
                      <span className="text-muted-foreground font-normal shrink-0">
                        ({(field.file.size / 1024).toFixed(1)} KB)
                      </span>
                    </button>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      {filesUploaded && extractionStatus[index] === "queued" && (() => {
                        const docId = queue.fileDocumentMap[index]?.document_id;
                        const info = docId ? queue.documentStatuses[docId] : undefined;
                        const pos = info?.queue_position;
                        return (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="w-3.5 h-3.5" />
                            Queued{pos ? ` (${pos} of ${queue.queueSize})` : ""}
                          </span>
                        );
                      })()}
                      {filesUploaded && extractionStatus[index] === "processing" && (
                        <span className="flex items-center gap-1 text-xs text-blue-600">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing…
                        </span>
                      )}
                      {filesUploaded && extractionStatus[index] === "extracting" && (
                        <span className="flex items-center gap-1 text-xs text-blue-600">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Extracting…
                        </span>
                      )}
                      {filesUploaded && extractionStatus[index] === "done" && (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <CheckCircle2 className="w-3.5 h-3.5" /> AI extracted
                        </span>
                      )}
                      {filesUploaded && extractionStatus[index] === "failed" && (
                        <>
                          <span className="flex items-center gap-1 text-xs text-amber-600">
                            <AlertCircle className="w-3.5 h-3.5" /> Extraction failed
                          </span>
                          <button
                            type="button"
                            title="Retry AI metadata extraction"
                            onClick={() => retryExtraction(index)}
                            disabled={isPending}
                            className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-brand-teal text-brand-teal hover:bg-brand-teal/10 transition-colors disabled:opacity-50"
                          >
                            <RefreshCw className="w-3 h-3" /> Re-extract
                          </button>
                        </>
                      )}
                      {/* Per-document indexing status */}
                      {savedDocIds[index] && (() => {
                        const info = indexingQueue.documentStatuses[savedDocIds[index]];
                        if (!info) return null;
                        if (info.status === "Queued") {
                          const pos = info.queue_position;
                          return (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="w-3.5 h-3.5" /> Queued for indexing{pos ? ` (${pos} of ${indexingQueue.queueSize})` : ""}
                            </span>
                          );
                        }
                        if (info.status === "Indexing") return (
                          <span className="flex items-center gap-1 text-xs text-blue-600">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Indexing…
                          </span>
                        );
                        if (info.status === "Done") return (
                          <span className="flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Indexed
                          </span>
                        );
                        if (info.status === "Error") return (
                          <span className="flex items-center gap-1 text-xs text-destructive">
                            <AlertCircle className="w-3.5 h-3.5" /> Indexing failed
                          </span>
                        );
                        return null;
                      })()}
                      {!filesUploaded && (
                        <button
                          type="button"
                          title="Remove file"
                          onClick={() => removeFile(index)}
                          className="rounded-full hover:bg-muted p-1"
                          disabled={isPending}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Metadata form — only shown after files are uploaded */}
                  {isExpanded && filesUploaded && (
                    <div className="px-4 pb-4 border-t">
                      <div className="grid grid-cols-2 gap-4 pt-4">
                        <div className="col-span-2 space-y-1.5">
                          <Label htmlFor={`doc-${index}-title`}>Title</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.title`}
                            render={({ field: f }) => (
                              <Input id={`doc-${index}-title`} placeholder="Document title" {...f} />
                            )}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`doc-${index}-authors`}>Author</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.authors`}
                            render={({ field: f }) => (
                              <Input id={`doc-${index}-authors`} placeholder="Author name" {...f} />
                            )}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`doc-${index}-year`}>Year</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.year`}
                            render={({ field: f }) => (
                              <Input id={`doc-${index}-year`} type="number" placeholder="e.g. 2024" {...f} />
                            )}
                          />
                        </div>

                        <div className="col-span-2 space-y-1.5">
                          <Label htmlFor={`doc-${index}-description`}>Description</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.description`}
                            render={({ field: f }) => (
                              <Textarea id={`doc-${index}-description`} placeholder="Brief description of the document" className="min-h-[60px]" {...f} />
                            )}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`doc-${index}-source_type`}>Source Type</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.source_type`}
                            render={({ field: f }) => (
                              <select id={`doc-${index}-source_type`} className={selectClassName} value={f.value} onChange={f.onChange} onBlur={f.onBlur}>
                                <option value="" disabled>Select source type</option>
                                {activeSourceTypes?.map((st) => <option key={st.id} value={st.name}>{st.name}</option>)}
                              </select>
                            )}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`doc-${index}-organization`}>Source Organisation</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.organization`}
                            render={({ field: f }) => (
                              <Input id={`doc-${index}-organization`} placeholder="Organisation name" {...f} />
                            )}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`doc-${index}-country`}>Country</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.country`}
                            render={({ field: f }) => (
                              <Input id={`doc-${index}-country`} placeholder="Country" {...f} />
                            )}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`doc-${index}-evidence_type`}>Evidence Type</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.evidence_type`}
                            render={({ field: f }) => (
                              <select id={`doc-${index}-evidence_type`} className={selectClassName} value={f.value} onChange={f.onChange} onBlur={f.onBlur}>
                                <option value="" disabled>Select evidence type</option>
                                {activeEvidenceTypes?.map((et) => <option key={et.id} value={et.name}>{et.name}</option>)}
                              </select>
                            )}
                          />
                        </div>

                        <div className="col-span-2 space-y-1.5">
                          <Label>Topic(s)</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.topic_ids`}
                            render={({ field: f }) => (
                              <TopicsMultiSelect
                                value={f.value}
                                onChange={f.onChange}
                                open={openTopicsPopoverIndex === index}
                                onOpenChange={(open) => setOpenTopicsPopoverIndex(open ? index : null)}
                              />
                            )}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`doc-${index}-data_retention`}>Data Retention</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.data_retention`}
                            render={({ field: f }) => (
                              <DatePicker
                                ref={activeDataRetentionRef}
                                id={`doc-${index}-data_retention`}
                                value={f.value}
                                onChange={f.onChange}
                                onValidityChange={setActiveDataRetentionInvalid}
                                placeholder="Select retention date..."
                              />
                            )}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`doc-${index}-url`}>URL</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.url`}
                            render={({ field: f }) => (
                              <Input id={`doc-${index}-url`} type="url" placeholder="Document URL" {...f} />
                            )}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`doc-${index}-language`}>Language</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.language`}
                            render={({ field: f }) => (
                              <Input id={`doc-${index}-language`} placeholder="e.g. English" {...f} />
                            )}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label>Ownership</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.ownership`}
                            render={({ field: f }) => (
                              <Select
                                value={f.value}
                                onValueChange={(val) => {
                                  f.onChange(val);
                                  // Pre-fill client from project when switching to Client
                                  if (val === "Client" && project?.clientIds?.[0]) {
                                    setValue(`documents.${index}.client_id`, project.clientIds[0]);
                                    setValue(`documents.${index}.client_name`, project.clientNames?.[0] ?? "");
                                  }
                                  if (val !== "Client") {
                                    setValue(`documents.${index}.client_id`, "");
                                    setValue(`documents.${index}.client_name`, "");
                                  }
                                }}
                                disabled={!!lockedOwnership}
                              >
                                <SelectTrigger><SelectValue placeholder="Select ownership" /></SelectTrigger>
                                <SelectContent>
                                  {(allowedOwnershipValues ?? OWNERSHIP_VALUES).map((v: string) => (
                                    <SelectItem key={v} value={v}>{v}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>

                        {/* Client combobox — shown when ownership = Client */}
                        <Controller
                          control={control}
                          name={`documents.${index}.ownership`}
                          render={({ field: ownershipField }) =>
                            ownershipField.value === "Client" ? (
                              <div className="space-y-1.5">
                                <Label>Client</Label>
                                {project?.clientIds?.[0] ? (
                                  // Project already has a client — read-only
                                  <div className="flex h-9 w-full items-center rounded-md border bg-muted px-3 text-sm text-muted-foreground">
                                    {project.clientNames?.[0] ?? "Assigned client"}
                                  </div>
                                ) : (
                                  <Controller
                                    control={control}
                                    name={`documents.${index}.client_name`}
                                    render={({ field: clientNameField }) => (
                                      <Popover
                                        open={openClientPopoverIndex === index}
                                        onOpenChange={(open) => {
                                          setOpenClientPopoverIndex(open ? index : null);
                                          if (!open) {
                                            setClientInputValue("");
                                            setClientSearch("");
                                            if (clientDebounceRef.current) clearTimeout(clientDebounceRef.current);
                                          }
                                        }}
                                      >
                                        <PopoverTrigger asChild>
                                          <Button
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={openClientPopoverIndex === index}
                                            className="w-full justify-between font-normal text-muted-foreground hover:text-muted-foreground hover:bg-inherit"
                                          >
                                            {clientNameField.value || "Select client..."}
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-full p-0" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
                                          <Command shouldFilter={false}>
                                            <CommandInput
                                              placeholder="Type to search clients..."
                                              value={openClientPopoverIndex === index ? clientInputValue : ""}
                                              onValueChange={(val) => {
                                                setClientInputValue(val);
                                                if (clientDebounceRef.current) clearTimeout(clientDebounceRef.current);
                                                clientDebounceRef.current = setTimeout(() => setClientSearch(val), 350);
                                              }}
                                            />
                                            <CommandList>
                                              {clientInputValue.trim().length === 0 ? (
                                                <CommandEmpty>Start typing to search clients...</CommandEmpty>
                                              ) : clientFetching ? (
                                                <CommandEmpty>Searching...</CommandEmpty>
                                              ) : clientResults.length === 0 ? (
                                                <CommandEmpty>No clients found.</CommandEmpty>
                                              ) : (
                                                <CommandGroup>
                                                  {clientResults.map((client) => (
                                                    <CommandItem
                                                      key={client.id}
                                                      value={client.id}
                                                      onSelect={() => {
                                                        setValue(`documents.${index}.client_id`, client.id);
                                                        clientNameField.onChange(client.name);
                                                        setOpenClientPopoverIndex(null);
                                                      }}
                                                    >
                                                      <Check className={cn("mr-2 h-4 w-4",
                                                        getValues(`documents.${index}.client_id`) === client.id ? "opacity-100" : "opacity-0"
                                                      )} />
                                                      {client.name}
                                                    </CommandItem>
                                                  ))}
                                                </CommandGroup>
                                              )}
                                            </CommandList>
                                          </Command>
                                        </PopoverContent>
                                      </Popover>
                                    )}
                                  />
                                )}
                              </div>
                            ) : null
                          }
                        />

                        <div className="col-span-2 space-y-1.5">
                          <Label htmlFor={`doc-${index}-tags`}>Tags</Label>
                          <Controller
                            control={control}
                            name={`documents.${index}.tags`}
                            render={({ field: f }) => (
                              <TagsInput id={`doc-${index}-tags`} value={f.value} onChange={f.onChange} placeholder="Add tags, e.g. NCCN, Guidelines, Lung Cancer" />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-3">
            {!filesUploaded ? (
              <Button
                type="button"
                className="bg-brand-teal text-white hover:bg-brand-teal/90 gap-1.5"
                disabled={fields.length === 0 || isUploading || isValidating}
                onClick={handleUpload}
              >
                {isUploading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4" /> Upload</>
                )}
              </Button>
            ) : (
              <Button
                type="submit"
                className="bg-brand-teal text-white hover:bg-brand-teal/90 gap-1.5"
                disabled={fields.length === 0 || isEnqueuingExtraction || queue.isPolling || isCheckingDuplicates || hasIncompleteExtraction || submitStage !== "idle"}
              >
                {isEnqueuingExtraction || queue.isPolling || hasIncompleteExtraction ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Extracting metadata...</>
                ) : isCheckingDuplicates ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Checking duplicates...</>
                ) : submitStage === "saving" ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                ) : submitStage === "indexing" || submitStage === "linking" ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Indexing...</>
                ) : (
                  <><Upload className="w-4 h-4" /> {continueLabel}</>
                )}
              </Button>
            )}
          </div>
        </form>
      )}

      {/* Duplicate confirmation dialog */}
      <DuplicateConfirmationDialog
        open={showDuplicateDialog}
        duplicateResults={duplicateResults}
        duplicateConfirmed={duplicateConfirmed}
        setDuplicateConfirmed={setDuplicateConfirmed}
        documents={getValues().documents}
        onConfirm={handleDuplicateConfirm}
        onCancel={handleDuplicateCancelAll}
      />
    </div>
  );
};

// Duplicate confirmation dialog component rendered alongside the form
const DuplicateConfirmationDialog = ({
  open,
  duplicateResults,
  duplicateConfirmed,
  setDuplicateConfirmed,
  documents,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  duplicateResults: DuplicateCheckResult[];
  duplicateConfirmed: Record<number, boolean>;
  setDuplicateConfirmed: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  documents: DocumentFormEntry[];
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const duplicates = duplicateResults.filter((r) => r.is_duplicate);

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Duplicate Files Detected</AlertDialogTitle>
          <AlertDialogDescription>
            The following files appear to already exist in the Evidence Base. Select the ones you still want to upload.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3 max-h-60 overflow-y-auto py-2">
          {duplicates.map((dup) => {
            const doc = documents[dup.index];
            if (!doc) return null;
            return (
              <label key={dup.index} className="flex items-start gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer">
                <Checkbox
                  checked={duplicateConfirmed[dup.index] ?? false}
                  onCheckedChange={(checked) =>
                    setDuplicateConfirmed((prev) => ({ ...prev, [dup.index]: !!checked }))
                  }
                  className="mt-0.5"
                />
                <div className="flex flex-col text-sm">
                  <span className="font-medium">{doc.title || doc.file.name}</span>
                  <span className="text-muted-foreground text-xs">
                    {doc.file.name} &middot; {doc.authors || "No author"} &middot; {doc.year || "No year"}
                  </span>
                </div>
              </label>
            );
          })}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel All</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>
            Proceed
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DocumentUploadForm;
